import React, { useState, useEffect, useRef } from 'react';
// Inline SVG components to match the vendor form's design
const CloseIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
// New Maximize Icon
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
// New Minimize Icon
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);
const API_URL = '/api';
const initialFields = {
  CategoryCode: '', 
  CategoryName: '', 
  Description: '', 
  Status: 'Active',
  department_id: '' // Added department_id field
};
const CategoryFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [fields, setFields] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('categoryInfo');
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false); // New state for maximize/minimize
  
  // Department-related state
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  
  // Refs for potential future file inputs
  const fileInputRefs = useRef({});
  
  // Function to normalize status value to ensure it's either 'Active' or 'Inactive'
  const normalizeStatus = (status) => {
    if (status === true || status === 1 || status === '1' || 
        String(status).toLowerCase() === 'active') {
      return 'Active';
    } else {
      return 'Inactive';
    }
  };
  
  // Function to fetch current user
  const fetchCurrentUser = async () => {
   try {
    const token = localStorage.getItem('authToken'); // store token after login
    if (!token) {
      setIsUserValid(false);
      setAuthError('No token found. Please log in again.');
      return false;
    }
    const userRes = await fetch(`${API_URL}/current-user`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        console.log('Current user fetched:', userData);
        return true;
      } else {
        console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  };
  
  // Function to fetch departments
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await fetch(`${API_URL}/departments`);
      const data = await response.json();
      console.log('Fetched departments:', data);
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };
  
  // Function to fetch screen configuration
  const fetchScreenConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/screens`);
      const data = await response.json();
      console.log('Fetched screen configurations:', data);
      
      // Find the category screen configuration
      const categoryScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'Categories'.toLowerCase()
      );
      
      console.log('Category screen configuration:', categoryScreen);
      if (categoryScreen) {
        setScreenConfig(categoryScreen);
        // Show department field if ShowOnHeader is 1
        const shouldShowDepartment = categoryScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        // If department field should be shown, fetch departments
        if (shouldShowDepartment) {
          fetchDepartments();
        }
      } else {
        // If no screen config found, default to showing department field
        setShowDepartmentField(true);
        fetchDepartments();
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      // Default to showing department field on error
      setShowDepartmentField(true);
      fetchDepartments();
    }
  };
  
  // Toggle maximize/minimize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  useEffect(() => {
    fetchCurrentUser();
  }, []);
  
 useEffect(() => {
  if (open) {
    fetchCurrentUser();
    fetchScreenConfig();
    
    if (isEdit && initialData) {
      console.log('Initial data received:', initialData);
      
      // Create a copy of initialData without created_by and updated_by
      const { CreatedBy, UpdatedBy, ...dataWithoutUserFields } = initialData;
      
      // Normalize the status to ensure it's either 'Active' or 'Inactive'
      const normalizedStatus = normalizeStatus(dataWithoutUserFields.Status);

      // Handle department_id - check if it's an array and get the first value
      let departmentId = '';
      if (initialData.DepartmentID) {
        departmentId = Array.isArray(initialData.DepartmentID) 
          ? initialData.DepartmentID[0] 
          : initialData.DepartmentID;
      } else if (initialData.department_id) {
        departmentId = Array.isArray(initialData.department_id) 
          ? initialData.department_id[0] 
          : initialData.department_id;
      }
      
      console.log('Processed department_id:', departmentId);
      
      setFields({ 
        CategoryCode: dataWithoutUserFields.CategoryCode || '', 
        CategoryName: dataWithoutUserFields.CategoryName || '', 
        Description: dataWithoutUserFields.Description || '', 
        Status: normalizedStatus,
        department_id: departmentId ? departmentId.toString() : '' 
      });
      
      // Ensure department field is shown when editing existing data
      if (departmentId) {
        setShowDepartmentField(true);
        fetchDepartments();
      }
    } else {
      // Generate next category code when creating new category
      setIsLoadingCode(true);
      generateNextCategoryCode()
        .then(nextCode => {
          setFields(prev => ({ 
            ...prev, 
            CategoryCode: nextCode,
            department_id: '' // Reset department_id for new entries
          }));
        })
        .catch(error => {
          console.error('Error generating category code:', error);
          setFields(prev => ({ 
            ...prev, 
            CategoryCode: 'CAT-001',
            department_id: '' // Reset department_id for new entries
          }));
        })
        .finally(() => {
          setIsLoadingCode(false);
        });
    }
    setErrors({});
  } else {
    // Reset department-related state when modal closes
    setShowDepartmentField(false);
    setScreenConfig(null);
    setDepartments([]);
  }
}, [open, initialData, isEdit]);
  
  const generateNextCategoryCode = async () => {
    try {
      const response = await fetch(`${API_URL}/categorylatestcode`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.nextCode;
    } catch (error) {
      console.error('Error fetching next category code:', error);
      return 'CAT-001';
    }
  };
  
  const handleChange = e => {
    setFields({ ...fields, [e.target.name]: e.target.value });
  };
  
  const validate = () => {
    const errs = {};
    if (!fields.CategoryName) errs.CategoryName = 'Required';
    if (!fields.Status) errs.Status = 'Required';
    // Add department validation only if the field is shown
    if (showDepartmentField && !fields.department_id) errs.department_id = 'Required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    // Check if we have a valid current user
    if (!isUserValid || !currentUser || !currentUser.UserID) {
      alert(authError || 'User information not available. Please refresh the page and log in again.');
      return;
    }
    
    const formData = {
      ...fields,
      Status: fields.Status === 'Active' ? 1 : 0
    };
    
    // Only append the relevant user ID field based on operation
    const userId = parseInt(currentUser.UserID);
    console.log('Using user ID:', userId);
    
    if (isEdit && initialData) {
      formData.UpdatedBy = userId;
      console.log('Appended UpdatedBy:', userId);
    } else {
      formData.CreatedBy = userId;
      console.log('Appended CreatedBy:', userId);
    }
    
    // Include department_id only if showDepartmentField is true
    if (showDepartmentField) {
      formData.department_id = fields.department_id;
    }
    
    try {
      onSubmit(formData);
    } catch (error) {
      console.error('Error saving category:', error);
      alert(error.message || 'An error occurred while saving the category');
    }
  };
  
  const tabs = [
    { id: 'categoryInfo', name: 'Category Info' },
  ];

  if (!open) return null;
  
  return (
    <div
      className={`fixed inset-0 z-[9999] flex justify-center transition-all duration-300 ease-in-out
        ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
    >
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none; /* Internet Explorer 10+ */
            scrollbar-width: none; /* Firefox */
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none; /* Safari and Chrome */
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      
      {/* This is the actual slide-in panel with fixed header and scrollable content */}
      <div 
        className={`bg-white shadow-2xl flex flex-col
          transition-all transform-gpu duration-300 ease-in-out
          ${open ? 'translate-x-0' : 'translate-x-full'}
          ${isMaximized ? 'w-full h-full max-w-none rounded-none' : 'w-full max-w-6xl h-full '}`}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">
              {isEdit ? 'Edit Category' : 'Create New Category'}
            </h2>
            <div className="flex items-center gap-2">
              {/* Maximize/Minimize Button */}
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1 rounded-full hover:bg-[#F1F5F9]" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon /> : <MaximizeIcon />}
              </button>
              {/* Close Button */}
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1 rounded-full hover:bg-[#F1F5F9]" 
                onClick={onClose}
                title="Close"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>
        
        {/* User validation warning */}
        {open && !isUserValid && (
          <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  {authError || 'User information not available. Please refresh the page and log in again.'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Fixed Tabs */}
        <div className="flex-shrink-0 px-8 pb-6">
          <div className="flex border-b border-[#BDDDFC] -mx-8 px-8 overflow-x-auto overflow-y-hidden whitespace-nowrap custom-scrollbar">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-4 px-4 -mb-px text-sm font-medium transition-colors focus:outline-none
                  ${activeTab === tab.id 
                    ? 'border-b-2 border-[#6A89A7] text-[#6A89A7]' 
                    : 'border-b-2 border-transparent text-[#A0AEC0] hover:text-[#6A89A7]'
                  }`}
              >
                {tab.name}
              </button>
            ))}
          </div>
          {/* Custom scrollbar indicator */}
          <div className="flex justify-center mt-2">
            <div className="w-16 h-1 rounded-full bg-[#cbd5e1]"></div>
          </div>
        </div>
        
        {/* Scrollable Content Area */}
        <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              {/* Category Information */}
              {activeTab === 'categoryInfo' && (
                <div className='space-y-4'>
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">Category Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className='flex flex-col gap-2'>
                      <label className="block text-sm font-medium text-[#64748b]">Category Code</label>
                      <input 
                        name="CategoryCode" 
                        value={isLoadingCode ? 'Loading...' : fields.CategoryCode} 
                        readOnly 
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] text-[#64748b] border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]" 
                      />
                    </div>
                    <div className='flex flex-col gap-2'>
                      <label className="block text-sm font-medium text-[#64748b]">Category Name *</label>
                      <input 
                        name="CategoryName" 
                        autoComplete="off"
                        value={fields.CategoryName} 
                        onChange={handleChange} 
                        className={`w-full border rounded-lg px-3 py-2 ${errors.CategoryName ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`} 
                      />
                      {errors.CategoryName && <span className="text-xs text-red-500">{errors.CategoryName}</span>}
                    </div>
                                        {/* Department Selection Field - Only shown if ShowOnHeader is 1 */}
                   {showDepartmentField && (
  <div className='flex flex-col gap-2'>
    <label className="block text-sm font-medium text-[#64748b]">Department *</label>
    <select
      name="department_id"
      value={fields.department_id}
      onChange={handleChange}
      className={`w-full rounded-md border ${errors.department_id ? 'border-red-400' : 'border-[#E2E8F0]'} bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase`}
      disabled={loadingDepartments}
    >
      <option value="">{loadingDepartments ? 'Loading departments...' : 'Select Department'}</option>
      {departments.map((dept) => (
        <option key={dept.DepartmentID} value={dept.DepartmentID}>
          {dept.DepartmentCode} - {dept.DepartmentName}
        </option>
      ))}
    </select>
    {errors.department_id && <span className="text-xs text-red-500">{errors.department_id}</span>}
  </div>
)}
                    
                    <div className='flex flex-col gap-2 md:col-span-2'>
                      <label className="block text-sm font-medium text-[#64748b]">Description</label>
                      <textarea 
                        name="Description" 
                        autoComplete="off"
                        value={fields.Description} 
                        onChange={handleChange} 
                        rows="3"
                        className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase" 
                      />
                    </div>
                    

                    <div className='flex flex-col gap-2'>
                      <label className="block text-sm font-medium text-[#64748b]">Status *</label>
                      <select 
                        name="Status" 
                        value={fields.Status} 
                        onChange={handleChange} 
                        className={`w-full border rounded-lg px-3 py-2 ${errors.Status ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                      {errors.Status && <span className="text-xs text-red-500">{errors.Status}</span>}
                    </div>
                    <div className='flex flex-col gap-2'>
                      <label className="block text-sm font-medium text-[#64748b]">Created Date</label>
                      <input 
                        value={new Date().toLocaleString()} 
                        readOnly 
                        className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] text-[#64748b] border-[#E2E8F0]" 
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
        
        {/* Fixed Footer with Buttons */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button 
              type="button" 
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#5a7997]" 
              disabled={loading || !isUserValid}
              onClick={handleSubmit}
            >
              {loading ? 'Saving...' : (isEdit ? 'Update & Save' : 'Continue & Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
export default CategoryFormModal;